import { Download, FileText, Globe2, Loader2, RefreshCw, Trash2, UploadCloud } from 'lucide-react';
import { useRef, useState } from 'react';
import type { ContextSource, OnboardingCapabilities } from './api';

export const documentAccept = '.pdf,.docx,.txt,.md';
const bytesLabel = (size: number) =>
  size < 1024 * 1024 ? `${Math.ceil(size / 1024)} KB` : `${(size / (1024 * 1024)).toFixed(1)} MB`;

export function validateDocuments(
  files: File[],
  capabilities: OnboardingCapabilities,
  sources: ContextSource[]
) {
  const current = sources.filter(
    (source) => source.kind === 'document' && source.status !== 'deleted'
  );
  if (current.length + files.length > capabilities.maxDocuments)
    return `Add up to ${capabilities.maxDocuments} documents per company.`;
  if (files.some((file) => !/\.(pdf|docx|txt|md)$/i.test(file.name)))
    return 'Use PDF, DOCX, TXT or Markdown documents.';
  const oversized = files.find((file) => file.size > capabilities.maxDocumentBytes);
  if (oversized)
    return `${oversized.name} is too large. Each file can be up to ${Math.round(capabilities.maxDocumentBytes / 1024 / 1024)} MB.`;
  const total =
    current.reduce((sum, source) => sum + (source.byteSize || 0), 0) +
    files.reduce((sum, file) => sum + file.size, 0);
  if (total > capabilities.maxTotalDocumentBytes)
    return `Keep the total document library below ${Math.round(capabilities.maxTotalDocumentBytes / 1024 / 1024)} MB.`;
  return '';
}

export default function SourceLibrary({
  sources,
  capabilities,
  busy = false,
  readOnly = false,
  showUpload = true,
  showSources = true,
  onUpload,
  onRemove,
  onInclude,
  onReplace,
  onDownload,
}: {
  sources: ContextSource[];
  capabilities: OnboardingCapabilities | null;
  busy?: boolean;
  readOnly?: boolean;
  showUpload?: boolean;
  showSources?: boolean;
  onUpload: (files: File[]) => Promise<void>;
  onRemove?: (source: ContextSource) => Promise<void>;
  onInclude?: (source: ContextSource, included: boolean) => Promise<void>;
  onReplace?: (source: ContextSource, file: File) => Promise<void>;
  onDownload?: (source: ContextSource) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const upload = async (files: File[]) => {
    if (!files.length || !capabilities || busy || readOnly) return;
    const validation = validateDocuments(files, capabilities, sources);
    setError(validation);
    if (validation) return;
    try {
      await onUpload(files);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to upload document.');
    }
  };
  const perform = async (action: () => Promise<void>) => {
    setError('');
    try {
      await action();
      setRemoving(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update document.');
    }
  };
  const visible = sources.filter((source) => source.status !== 'deleted');
  return (
    <section className="context-source-library" aria-label="Company sources">
      {showUpload && !readOnly && capabilities?.documentsEnabled && (
        <>
          <input
            ref={input}
            type="file"
            accept={documentAccept}
            multiple
            aria-label="Upload company documents"
            className="context-file-input"
            disabled={busy}
            onChange={(event) => {
              void upload(Array.from(event.target.files || []));
              event.target.value = '';
            }}
          />
          <button
            type="button"
            className={`context-dropzone ${dragging ? 'is-dragging' : ''}`}
            disabled={busy}
            onClick={() => input.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              void upload(Array.from(event.dataTransfer.files));
            }}
          >
            {busy ? <Loader2 size={24} className="context-spin" /> : <UploadCloud size={26} />}
            <strong>
              {busy ? 'Updating company sources…' : 'Drop documents here, or choose files'}
            </strong>
            <span>Company profiles, capability statements, business plans or presentations</span>
            <small>
              PDF, Word (DOCX), TXT or Markdown ·{' '}
              {Math.round(capabilities.maxDocumentBytes / 1024 / 1024)} MB per file · up to{' '}
              {capabilities.maxDocuments} files
            </small>
          </button>
          <p className="context-help">
            Documents stay in this company’s private library. Only included sources inform its AI
            context.
          </p>
        </>
      )}
      {showUpload && !readOnly && capabilities && !capabilities.documentsEnabled && (
        <p className="context-help">
          Document uploads are unavailable right now. You can continue with your website and own
          description.
        </p>
      )}
      {error && (
        <p className="context-error" role="alert">
          {error}
        </p>
      )}
      {showSources && visible.length > 0 && (
        <ul className="context-source-list">
          {(expanded ? visible : visible.slice(0, 8)).map((source) => (
            <li key={source.id}>
              <span className="context-source-icon">
                {source.kind === 'website' ? <Globe2 size={18} /> : <FileText size={18} />}
              </span>
              <div className="context-source-details">
                <strong>{source.label || source.fileName || source.url}</strong>
                <span>
                  {source.kind === 'website'
                    ? 'Website'
                    : source.byteSize
                      ? bytesLabel(source.byteSize)
                      : 'Document'}{' '}
                  ·{' '}
                  {source.status === 'ready'
                    ? 'Extracted'
                    : source.status === 'failed'
                      ? 'Could not read'
                      : 'Reading source…'}
                </span>
                {source.error && <p className="context-source-error">{source.error}</p>}
                {(source.metadata?.truncated || source.metadata?.partial) && (
                  <p className="context-source-coverage">
                    Partially read
                    {typeof source.metadata.processedPages === 'number' &&
                    typeof source.metadata.pageCount === 'number'
                      ? ` · ${source.metadata.processedPages} of ${source.metadata.pageCount} pages`
                      : ''}
                  </p>
                )}
                {Array.isArray(source.metadata?.warnings) &&
                  source.metadata.warnings
                    .filter((warning) => typeof warning === 'string')
                    .map((warning, index) => (
                      <p className="context-source-coverage" key={index}>
                        {String(warning)}
                      </p>
                    ))}
                {source.url && (
                  <a href={source.url} target="_blank" rel="noreferrer">
                    View source
                  </a>
                )}
                {!readOnly && onInclude && (
                  <label className="context-source-include">
                    <input
                      type="checkbox"
                      checked={source.includedInContext}
                      disabled={busy || source.status === 'failed'}
                      onChange={(event) =>
                        void perform(() => onInclude(source, event.target.checked))
                      }
                    />{' '}
                    Use in AI context
                  </label>
                )}
              </div>
              <div className="context-source-actions">
                {source.canDownload && onDownload && (
                  <button
                    type="button"
                    className="context-icon-button"
                    title="Download original"
                    aria-label={`Download ${source.label}`}
                    disabled={busy}
                    onClick={() => void perform(() => onDownload(source))}
                  >
                    <Download size={17} />
                  </button>
                )}
                {!readOnly && source.kind === 'document' && onReplace && (
                  <label
                    className="context-icon-button context-replace-button"
                    title="Replace document"
                  >
                    <RefreshCw size={16} />
                    <span className="sr-only">Replace {source.label}</span>
                    <input
                      type="file"
                      accept={documentAccept}
                      disabled={busy}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (!file || !capabilities) return;
                        const validation = validateDocuments(
                          [file],
                          capabilities,
                          sources.filter((item) => item.id !== source.id)
                        );
                        if (validation) {
                          setError(validation);
                          return;
                        }
                        void perform(() => onReplace(source, file));
                      }}
                    />
                  </label>
                )}
                {!readOnly && onRemove && (
                  <button
                    type="button"
                    className="context-icon-button"
                    title="Remove source"
                    aria-label={`Remove ${source.label}`}
                    disabled={busy}
                    onClick={() => setRemoving(source.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              {removing === source.id && onRemove && (
                <div className="context-remove-confirm">
                  <p>Remove this source from the company’s library and AI context?</p>
                  <button
                    type="button"
                    className="context-button context-button-small"
                    disabled={busy}
                    onClick={() => void perform(() => onRemove(source))}
                  >
                    Remove source
                  </button>
                  <button
                    type="button"
                    className="context-button context-button-secondary context-button-small"
                    onClick={() => setRemoving(null)}
                  >
                    Keep source
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {showSources && visible.length > 8 && (
        <button
          type="button"
          className="context-text-button"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show fewer sources' : `Show all ${visible.length} sources`}
        </button>
      )}
    </section>
  );
}
